import { useMemo, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { apiClient } from '@services/api'

type TxType = 'receipt' | 'payment'
type Mode = 'Cash' | 'Net Banking' | 'UPI' | 'Cheque'
interface OpenBill { id: number; billNo: string; party: string; partyName: string; partyId: number; direction: 'receivable' | 'payable'; date: string; dueDate?: string | null; amount: number; paid: number; balance: number; status: 'Unpaid' | 'Partial'; overdueDays: number }
interface Tx { id: string; transactionId: string; type: TxType; party: string; referenceBill: string; referenceId: number; amount: number; mode: Mode; referenceNo: string | null; date: string; recordedBy: string }

const today = () => new Date().toISOString().split('T')[0]
const formatINR = (n: number) => `₹${Math.round(n).toLocaleString('en-IN')}`
const formatDate = (iso: string) => new Date(`${iso}T00:00:00`).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
const statusBadge: Record<string, string> = { Paid: 'badge-full', Partial: 'badge-partial', Unpaid: 'badge-unpaid' }

export function Transactions() {
  const queryClient = useQueryClient()
  const [type, setType] = useState<TxType>('receipt')
  const [partyId, setPartyId] = useState('')
  const [billId, setBillId] = useState('')
  const [amount, setAmount] = useState('')
  const [date, setDate] = useState(today())
  const [mode, setMode] = useState<Mode>('Cash')
  const [referenceNo, setReferenceNo] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [notice, setNotice] = useState<{ kind: 'success' | 'error'; text: string; link?: string } | null>(null)
  const [saving, setSaving] = useState(false)
  const [logFilter, setLogFilter] = useState<'all' | TxType>('all')
  const [logSearch, setLogSearch] = useState('')

  const { data: openData, refetch: refetchOpen } = useQuery({ queryKey: ['outstanding-bills'], queryFn: () => apiClient.get<any>('/outstanding-bills') })
  const { data: storesData } = useQuery({ queryKey: ['stores'], queryFn: () => apiClient.get<any>('/stores') })
  const { data: suppliersData } = useQuery({ queryKey: ['suppliers-list'], queryFn: () => apiClient.get<any>('/suppliers') })
  const logQs = new URLSearchParams({ ...(logFilter !== 'all' ? { type: logFilter } : {}), ...(logSearch ? { search: logSearch } : {}), limit: '25' }).toString()
  const { data: txData, refetch: refetchTx } = useQuery({ queryKey: ['transactions', logQs], queryFn: () => apiClient.get<any>(`/transactions?${logQs}`) })

  const openBills: OpenBill[] = openData?.data?.items || []
  const openSummary = openData?.data?.summary
  const txs: Tx[] = txData?.data?.items || []
  const txSummary = txData?.data?.summary

  const direction = type === 'receipt' ? 'receivable' : 'payable'
  const billsForType = useMemo(() => openBills.filter((b) => b.direction === direction), [openBills, direction])
  // Every store / supplier is listed; the ones with unpaid bills come first with their balance.
  const masterList: Array<{ id: number; name: string }> = (type === 'receipt' ? storesData?.data?.items : suppliersData?.data?.items) || []
  const parties = useMemo(() => {
    const m = new Map<number, { id: number; name: string; balance: number; bills: OpenBill[] }>()
    for (const p of masterList) m.set(p.id, { id: p.id, name: p.name.split(' — ')[0], balance: 0, bills: [] })
    for (const b of billsForType) {
      const cur = m.get(b.partyId) || { id: b.partyId, name: b.partyName, balance: 0, bills: [] }
      cur.balance += b.balance
      cur.bills.push(b)
      m.set(b.partyId, cur)
    }
    return [...m.values()].sort((a, b) => b.balance - a.balance || a.name.localeCompare(b.name))
  }, [billsForType, masterList])
  const party = parties.find((p) => String(p.id) === partyId) || null
  const bill = billsForType.find((b) => String(b.id) === billId) || null
  const amt = Number(amount)

  const clearError = (k: string) => setErrors((e) => { if (!e[k]) return e; const n = { ...e }; delete n[k]; return n })
  const switchType = (t: TxType) => { setType(t); setPartyId(''); setBillId(''); setAmount(''); setErrors({}); setNotice(null) }
  const chooseParty = (id: string) => { setPartyId(id); setBillId(''); setAmount(''); clearError('party'); clearError('bill') }
  const chooseBill = (id: string) => {
    setBillId(id)
    const b = billsForType.find((x) => String(x.id) === id)
    setAmount(b ? String(b.balance) : '')
    clearError('bill'); clearError('amount')
  }
  const pickFromList = (b: OpenBill) => {
    setType(b.direction === 'receivable' ? 'receipt' : 'payment')
    setPartyId(String(b.partyId))
    setBillId(String(b.id))
    setAmount(String(b.balance))
    setErrors({})
    setNotice(null)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const calcHint = () => {
    if (!bill || !(amt > 0)) return null
    if (amt > bill.balance + 0.005) return { kind: 'error', text: `Amount exceeds the balance of ${formatINR(bill.balance)} on ${bill.billNo}` }
    if (Math.abs(amt - bill.balance) < 0.005) return { kind: 'calc', text: `→ This will fully settle ${bill.billNo} — status will move from ${bill.status} to Paid.` }
    return { kind: 'calc', text: `→ Partial ${type === 'receipt' ? 'receipt' : 'payment'} — ${formatINR(bill.balance - amt)} will remain on ${bill.billNo}${bill.status === 'Unpaid' ? ' (status moves from Unpaid to Partial)' : ''}.` }
  }
  const hint = calcHint()

  const submit = async () => {
    setNotice(null)
    const e: Record<string, string> = {}
    if (!party) e.party = `Select a ${type === 'receipt' ? 'store' : 'supplier'}`
    if (!bill) e.bill = 'Select the bill this is against'
    if (!(amt > 0)) e.amount = 'Amount must be greater than 0'
    else if (bill && amt > bill.balance + 0.005) e.amount = `Cannot exceed the balance of ${formatINR(bill.balance)}`
    if (!date) e.date = 'Date is required'
    else if (date > today()) e.date = 'Date cannot be in the future'
    if (mode === 'Cheque' && !referenceNo.trim()) e.referenceNo = 'Cheque number is required'
    setErrors(e)
    if (Object.keys(e).length) return
    setSaving(true)
    try {
      const url = type === 'receipt' ? `/invoices/${bill!.id}/collections` : `/purchase-bills/${bill!.id}/payments`
      const res = await apiClient.post<any>(url, { amount: amt, date, mode, referenceNo: referenceNo.trim() })
      setNotice({ kind: 'success', text: res.message, link: type === 'receipt' ? `/dashboard/sales/invoices/${bill!.id}` : `/dashboard/procurement/purchase-bills/${bill!.id}` })
      setBillId(''); setAmount(''); setReferenceNo(''); setPartyId('')
      await Promise.all([refetchOpen(), refetchTx()])
      queryClient.invalidateQueries({ queryKey: ['stores'] })
      queryClient.invalidateQueries({ queryKey: ['home-dashboard'] })
      queryClient.invalidateQueries({ queryKey: ['collections-summary'] })
    } catch (err: any) {
      setNotice({ kind: 'error', text: err.response?.data?.message || 'Could not record the transaction' })
    } finally {
      setSaving(false)
    }
  }

  return (
    <div>
      <div className="hdr">
        <h1>Transactions</h1>
        <p>Record any receipt or payment — cash, net banking, UPI, or cheque — against a bill.</p>
      </div>

      {notice && (
        <div className={`alert ${notice.kind === 'success' ? 'alert-success' : 'alert-error'}`}>
          {notice.text}{notice.link && <> · <Link to={notice.link} className="font-semibold underline">Open</Link></>}
        </div>
      )}

      <div className="card">
        <h2>New Transaction</h2>
        <label>Type</label>
        <div className="row2">
          <button type="button" className={type === 'receipt' ? 'btn-primary' : 'btn-ghost'} onClick={() => switchType('receipt')}>Receipt (from Store)</button>
          <button type="button" className={type === 'payment' ? 'btn-primary' : 'btn-ghost'} onClick={() => switchType('payment')}>Payment (to Supplier)</button>
        </div>

        <div className="grid grid-cols-3 gap-3 mt-3">
          <div>
            <label>{type === 'receipt' ? 'Store' : 'Supplier'}</label>
            <select value={partyId} onChange={(e) => chooseParty(e.target.value)} className={errors.party ? 'error' : ''}>
              <option value="">— Select {type === 'receipt' ? 'store' : 'supplier'} —</option>
              {parties.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.bills.length ? `${p.name} — Balance ${formatINR(p.balance)} on ${p.bills.length === 1 ? p.bills[0].billNo : `${p.bills.length} bills`}` : `${p.name} — no unpaid ${type === 'receipt' ? 'invoices' : 'bills'}`}
                </option>
              ))}
            </select>
            {errors.party && <div className="hint-error">{errors.party}</div>}
            {parties.length === 0 && <div className="hint">No {type === 'receipt' ? 'stores' : 'suppliers'} yet — add them from {type === 'receipt' ? 'Billing' : 'Purchase Entry'} or Settings.</div>}
            {parties.length > 0 && billsForType.length === 0 && <div className="hint">Nothing outstanding {type === 'receipt' ? 'from stores' : 'to suppliers'} right now — {type === 'receipt' ? 'receipts are recorded against unpaid credit invoices, so generate the invoice in Billing first' : 'payments are recorded against unpaid supplier bills, so enter the bill in Purchase Entry first'}.</div>}
          </div>
          <div>
            <label>Reference Bill</label>
            <select value={billId} onChange={(e) => chooseBill(e.target.value)} className={errors.bill ? 'error' : ''} disabled={!party}>
              <option value="">— Select bill —</option>
              {(party?.bills || []).map((b) => (
                <option key={b.id} value={b.id}>
                  {b.billNo} — {formatINR(b.amount)} total, {formatINR(b.balance)} balance{b.overdueDays > 0 ? ` · ${b.overdueDays}d overdue` : ''}
                </option>
              ))}
            </select>
            {errors.bill && <div className="hint-error">{errors.bill}</div>}
            {party && party.bills.length === 0 && <div className="hint">{party.name} has no unpaid {type === 'receipt' ? 'invoices — nothing to receive against' : 'bills — nothing to pay against'}.</div>}
            {bill && <div className="hint">{formatDate(bill.date)} · paid {formatINR(bill.paid)} so far · <span className={`badge ${statusBadge[bill.status]}`}>{bill.status}</span></div>}
          </div>
          <div>
            <label>Amount</label>
            <input type="number" min={1} step={0.01} value={amount} onChange={(e) => { setAmount(e.target.value); clearError('amount') }} className={errors.amount ? 'error' : ''} placeholder={bill ? String(bill.balance) : ''} />
            {errors.amount && <div className="hint-error">{errors.amount}</div>}
            {bill && amt !== bill.balance && <button type="button" className="btn-ghost btn-xs mt-1" onClick={() => setAmount(String(bill.balance))}>Use full balance {formatINR(bill.balance)}</button>}
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div>
            <label>Date</label>
            <input type="date" value={date} max={today()} onChange={(e) => { setDate(e.target.value); clearError('date') }} className={errors.date ? 'error' : ''} />
            {errors.date && <div className="hint-error">{errors.date}</div>}
          </div>
          <div>
            <label>Mode</label>
            <select value={mode} onChange={(e) => { setMode(e.target.value as Mode); clearError('referenceNo') }}>
              <option>Cash</option><option>Net Banking</option><option>UPI</option><option>Cheque</option>
            </select>
          </div>
          <div>
            <label>Reference No. <small style={{ fontWeight: 400, color: 'var(--ink-soft)' }}>— {mode === 'Cheque' ? 'cheque no., required' : mode === 'Net Banking' ? 'UTR, optional' : mode === 'UPI' ? 'UPI ref, optional' : 'optional'}</small></label>
            <input value={referenceNo} onChange={(e) => { setReferenceNo(e.target.value); clearError('referenceNo') }} className={errors.referenceNo ? 'error' : ''} />
            {errors.referenceNo && <div className="hint-error">{errors.referenceNo}</div>}
          </div>
        </div>

        {hint && <div className={hint.kind === 'error' ? 'hint-error' : 'hint calc'}>{hint.text}</div>}
        {!hint && <div className="hint">Receipts post to Collections and reduce the store's credit; payments reduce what you owe the supplier — both feed the ledger and dashboard instantly.</div>}
        <div className="btnrow">
          <button type="button" className="btn-accent" onClick={submit} disabled={saving}>{saving ? 'Recording…' : 'Record Transaction'}</button>
        </div>
      </div>

      <div className="grid2 mt-[18px]">
        <div className="card">
          <h2>Unpaid &amp; Partially-Paid Bills <small>— both directions</small></h2>
          {openSummary && <div className="hint" style={{ marginTop: -6, marginBottom: 8 }}>Receivable {formatINR(openSummary.receivable)} on {openSummary.receivableCount} invoice{openSummary.receivableCount === 1 ? '' : 's'} · Payable {formatINR(openSummary.payable)} on {openSummary.payableCount} bill{openSummary.payableCount === 1 ? '' : 's'}</div>}
          <table>
            <thead><tr><th>Bill</th><th>Party</th><th className="num">Balance</th><th>Status</th></tr></thead>
            <tbody>
              {openBills.length === 0 && <tr><td colSpan={4} className="text-ink-soft">Nothing outstanding in either direction. 🎉</td></tr>}
              {openBills.map((b) => (
                <tr key={`${b.direction}-${b.id}`} onClick={() => pickFromList(b)} style={{ cursor: 'pointer' }} title="Click to record against this bill">
                  <td className="font-semibold">{b.billNo}{b.overdueDays > 0 && <div className="text-[10.5px] text-danger font-normal">{b.overdueDays}d overdue</div>}</td>
                  <td>{b.party}</td>
                  <td className="num">{formatINR(b.balance)}</td>
                  <td><span className={`badge ${statusBadge[b.status]}`}>{b.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="hint" style={{ marginTop: 8 }}>Click a row to pre-fill the form with that bill's balance.</div>
        </div>

        <div className="card">
          <div className="field-label-row">
            <h2 style={{ marginBottom: 0 }}>Recent Transactions</h2>
            <div className="flex gap-1">
              {(['all', 'receipt', 'payment'] as const).map((f) => (
                <button key={f} type="button" className={`chip ${logFilter === f ? 'on' : ''}`} onClick={() => setLogFilter(f)}>{f === 'all' ? 'All' : f === 'receipt' ? 'Receipts' : 'Payments'}</button>
              ))}
            </div>
          </div>
          {txSummary && <div className="hint" style={{ marginTop: 4 }}>Today: received {formatINR(txSummary.receiptsToday)} · paid {formatINR(txSummary.paymentsToday)}</div>}
          <input type="text" placeholder="Search party, bill or reference…" value={logSearch} onChange={(e) => setLogSearch(e.target.value)} style={{ marginTop: 8 }} />
          <table>
            <thead><tr><th>Txn</th><th>Date</th><th>Party</th><th>Against</th><th className="num">Amount</th><th>Mode</th></tr></thead>
            <tbody>
              {txs.length === 0 && <tr><td colSpan={6} className="text-ink-soft">No transactions yet.</td></tr>}
              {txs.map((t) => (
                <tr key={t.id}>
                  <td><span className={`badge ${t.type === 'receipt' ? 'badge-cash' : 'badge-credit'}`}>{t.type === 'receipt' ? 'IN' : 'OUT'}</span> <span className="font-medium">{t.transactionId}</span></td>
                  <td>{formatDate(t.date)}</td>
                  <td>{t.party}</td>
                  <td><Link to={t.type === 'receipt' ? `/dashboard/sales/invoices/${t.referenceId}` : `/dashboard/procurement/purchase-bills/${t.referenceId}`} className="text-primary-500 hover:underline">{t.referenceBill}</Link></td>
                  <td className={`num font-semibold ${t.type === 'receipt' ? 'text-good' : ''}`}>{t.type === 'receipt' ? '+' : '−'}{formatINR(t.amount)}</td>
                  <td>{t.mode}{t.referenceNo ? <div className="text-[10.5px] text-ink-soft">{t.referenceNo}</div> : null}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {txSummary && txSummary.byMode && (
            <div className="hint" style={{ marginTop: 8 }}>
              By mode (filtered): {txSummary.byMode.filter((m: any) => m.receipts || m.payments).map((m: any) => `${m.mode} in ${formatINR(m.receipts)} / out ${formatINR(m.payments)}`).join(' · ') || '—'}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
